/**
 * render/transition.ts — Zone Transition Renderer
 *
 * This is the space between worlds. The moment a traveler steps through a
 * waystone or boards a ferry and the familiar fades behind them. It should
 * feel like a held breath — anticipation, warmth, the sense that something
 * new is about to unfold.
 *
 * Four phases compose the journey:
 *   1. Fade Out   — the old world dims, a vignette draws the eye inward
 *   2. Traveling  — motes drift, a progress thread stretches across the dark
 *   3. Zone Title — the destination announces itself, earned and ceremonial
 *   4. Fade In    — the new world blooms beneath, ready to be inhabited
 *
 * Every color is warm. Every timing curve has intention. The particles breathe.
 * The typography is decorative without being fussy. This should make players
 * want to travel just to watch it happen.
 *
 * "Does this feel like home?" — even in transit, the answer should be yes.
 */

import type { GameState } from "../engine/state.js";

// ─── Palette ──────────────────────────────────────────────────────────────────

/** Warm neutral tones that anchor every phase */
const P = {
  blackWarm:    "#0a0806",
  travelText:   "#8a7860",
  zoneName:     "#f5e6d0",
  zoneDesc:     "#c8b898",
  goldLine:     "#c8a050",
  tipText:      "#6a5a40",
  progressGold: "#ffe0a0",
  moteGold:     "#ffe0a0",
  moteWhite:    "rgba(255,255,240,0.10)",
  dangerRed:    "#8a2010",
  dangerText:   "#c04030",
} as const;

// ─── Phase Durations (ms) ─────────────────────────────────────────────────────

const FADE_OUT_MS   = 800;
const ZONE_TITLE_MS = 2000;
const FADE_IN_MS    = 800;
const TRAVELING_MIN = 2000;
const TRAVELING_MAX = 32000;  // Danger zones can take 35s — let the traveling phase fill most of it

const TIP_CYCLE_MS  = 3000;  // ms between tip rotations

// ─── Travel Tips ─────────────────────────────────────────────────────────────

const TRAVEL_TIPS: string[] = [
  "Every room has a mood. Furniture shapes how a space feels.",
  "Your Aeterna remembers conversations. Ask about last time.",
  "Pinned notes in Scribe are always at the top.",
  "Hold Shift+D for the hPad instead of the hPhone.",
  "Creatures bond faster if you visit them daily.",
  "The Lantern Market has seasonal items. Check back often.",
  "Your room's warmth affects the Aeterna's mood.",
  "Press R to rotate furniture before placing it.",
  "Some furniture interacts with furniture nearby.",
  "The harbor has the freshest fish for recipes.",
  "Waystones let you fast-travel to zones you've discovered.",
  "Night time reveals secrets that daylight hides.",
  "The Aeterna can read your Scribe notes.",
  "Crafting at Ironforge produces unique furniture.",
  "Plant seeds grow differently depending on the season.",
  "A well-lit room draws more visitors during open hours.",
  "Rare materials drop more often in high-danger zones.",
  "Your journal records every zone you've visited.",
];

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface TransitionConfig {
  fromZoneName: string;
  toZoneName: string;
  toZoneDescription: string;
  toZoneColor: string;        // primary hex color of destination zone
  toZoneDangerLevel: number;  // 0–3
  travelDuration: number;     // total ms for the traveling phase (clamped internally)
  fromZoneColor: string;      // primary hex color of origin zone
}

interface TransitionParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseAlpha: number;
  color: string;
  life: number;    // 0–1
  maxLife: number;
  phase: number;   // random offset for sine breathing
  isSpark: boolean;
}

// ─── Easing Functions ─────────────────────────────────────────────────────────

function easeInQuad(t: number): number {
  return t * t;
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// ─── Color Utilities ──────────────────────────────────────────────────────────

/**
 * Parse a 6-digit hex string (#rrggbb or rrggbb) into [r, g, b] 0–255 integers.
 * Returns [0,0,0] on parse failure so rendering never throws.
 */
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace(/^#/, "");
  if (clean.length !== 6) return [0, 0, 0];
  const n = parseInt(clean, 16);
  if (isNaN(n)) return [0, 0, 0];
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");
}

/**
 * Darken a hex color by multiplying each RGB channel by (1 - amount).
 * amount=0 → unchanged, amount=1 → black.
 */
function darkenColor(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - Math.max(0, Math.min(1, amount));
  return rgbToHex(r * f, g * f, b * f);
}

/**
 * Linearly interpolate between two hex colors.
 * t=0 → color a, t=1 → color b.
 */
function lerpColor(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(
    ar + (br - ar) * t,
    ag + (bg - ag) * t,
    ab + (bb - ab) * t
  );
}

/** Convert hex to a CSS rgba() string with the given alpha */
function hexAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
}

// ─── Particle Helpers ─────────────────────────────────────────────────────────

const MOTE_COLORS = [P.moteGold, "rgba(255,240,200,0.3)", P.moteWhite];

function randomMoteColor(toZoneColor: string): string {
  const roll = Math.random();
  if (roll < 0.45) return P.moteGold;
  if (roll < 0.75) return hexAlpha(toZoneColor, 0.30);
  return P.moteWhite;
}

function spawnParticle(
  width: number,
  height: number,
  toZoneColor: string,
  fromEdge: boolean
): TransitionParticle {
  const isSpark = Math.random() < 0.08;
  const size = isSpark
    ? 4 + Math.random() * 1.5
    : 1 + Math.random() * 2;

  // Spawn across the left edge when fromEdge, or randomly anywhere
  const x = fromEdge ? -size : Math.random() * width;
  const y = Math.random() * height;

  const speed = 0.18 + Math.random() * 0.22;
  const vx = speed * (0.85 + Math.random() * 0.3);
  const vy = -(speed * (0.05 + Math.random() * 0.25));

  const maxLife = 6000 + Math.random() * 6000; // ms

  return {
    x,
    y,
    vx,
    vy,
    size,
    baseAlpha: isSpark ? 0.08 + Math.random() * 0.10 : 0.10 + Math.random() * 0.30,
    color: randomMoteColor(toZoneColor),
    life: maxLife,
    maxLife,
    phase: Math.random() * Math.PI * 2,
    isSpark,
  };
}

function respawnAtLeftEdge(
  p: TransitionParticle,
  width: number,
  height: number,
  toZoneColor: string
): void {
  const fresh = spawnParticle(width, height, toZoneColor, true);
  Object.assign(p, fresh);
}

// ─── TransitionRenderer ───────────────────────────────────────────────────────

export class TransitionRenderer {
  private config: TransitionConfig | null = null;
  private phase: "fade_out" | "traveling" | "zone_title" | "fade_in" | "idle" = "idle";
  private phaseTimer: number = 0;      // ms elapsed in current phase
  private totalTimer: number = 0;      // ms elapsed since transition started
  private particles: TransitionParticle[] = [];
  private tips: string[] = [...TRAVEL_TIPS];
  private currentTip: string = "";
  private tipTimer: number = 0;        // ms since last tip change
  private tipAlpha: number = 1;        // 0–1, for fade in/out
  private tipFading: boolean = false;  // true while fading out before swap
  private nextTip: string = "";        // queued tip waiting for fade-out to finish

  // Cached per-frame values to avoid recalculation inside draw helpers
  private _width: number = 0;
  private _height: number = 0;
  private _travelingDuration: number = 0;

  // ─── Public API ──────────────────────────────────────────────────────────────

  /** Begin a new zone transition. */
  start(config: TransitionConfig): void {
    this.config = config;
    // The traveling phase fills the gap between the fixed phases so the total
    // transition duration matches the actual travel time
    const fixedMs = FADE_OUT_MS + ZONE_TITLE_MS + FADE_IN_MS;
    const idealTraveling = config.travelDuration - fixedMs;
    this._travelingDuration = Math.max(TRAVELING_MIN, Math.min(TRAVELING_MAX, idealTraveling));
    this.phase = "fade_out";
    this.phaseTimer = 0;
    this.totalTimer = 0;
    this.particles = [];
    this.tipTimer = 0;
    this.tipFading = false;
    this.tipAlpha = 1;
    this.currentTip = this._pickTip();
    this.nextTip = "";
  }

  /** Update animation state. Returns true when transition is complete. */
  update(dt: number): boolean {
    if (this.phase === "idle" || this.config === null) return false;

    this.phaseTimer += dt;
    this.totalTimer += dt;

    // ── Phase advancement ────────────────────────────────────────────────────
    switch (this.phase) {
      case "fade_out":
        if (this.phaseTimer >= FADE_OUT_MS) {
          this.phase = "traveling";
          this.phaseTimer -= FADE_OUT_MS;
          // Seed initial particles
          this._seedParticles();
        }
        break;

      case "traveling":
        this._updateParticles(dt);
        this._updateTip(dt);
        if (this.phaseTimer >= this._travelingDuration) {
          this.phase = "zone_title";
          this.phaseTimer -= this._travelingDuration;
        }
        break;

      case "zone_title":
        if (this.phaseTimer >= ZONE_TITLE_MS) {
          this.phase = "fade_in";
          this.phaseTimer -= ZONE_TITLE_MS;
        }
        break;

      case "fade_in":
        if (this.phaseTimer >= FADE_IN_MS) {
          this.phase = "idle";
          this.config = null;
          return true;
        }
        break;
    }

    return false;
  }

  /** Draw the transition screen to canvas. */
  draw(ctx: CanvasRenderingContext2D, width: number, height: number, _state: GameState): void {
    if (this.phase === "idle" || this.config === null) return;

    this._width = width;
    this._height = height;

    switch (this.phase) {
      case "fade_out":   this._drawFadeOut(ctx, width, height);   break;
      case "traveling":  this._drawTraveling(ctx, width, height); break;
      case "zone_title": this._drawZoneTitle(ctx, width, height); break;
      case "fade_in":    this._drawFadeIn(ctx, width, height);    break;
    }
  }

  /** Whether a transition is currently active. */
  isActive(): boolean {
    return this.phase !== "idle";
  }

  // ─── Phase Renderers ─────────────────────────────────────────────────────────

  private _drawFadeOut(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const t = Math.min(1, this.phaseTimer / FADE_OUT_MS);
    const eased = easeInQuad(t);

    // Warm dark overlay
    ctx.save();
    ctx.globalAlpha = eased;
    ctx.fillStyle = P.blackWarm;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // Closing vignette — grows inward as eased increases
    const maxRadius = Math.sqrt(w * w + h * h) * 0.5;
    const innerRadius = maxRadius * (1 - eased * 0.85);
    const cx = w * 0.5;
    const cy = h * 0.5;

    ctx.save();
    const vignette = ctx.createRadialGradient(cx, cy, innerRadius * 0.4, cx, cy, innerRadius);
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(1, `rgba(8,5,3,${(eased * 0.7).toFixed(3)})`);
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  private _drawTraveling(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const cfg = this.config!;
    const progress = Math.min(1, this.phaseTimer / this._travelingDuration);

    // ── Background gradient (top-left → bottom-right) ──────────────────────
    const fromDark = darkenColor(cfg.fromZoneColor, 0.70);
    const toDark   = darkenColor(cfg.toZoneColor, 0.70);
    const midColor = lerpColor(fromDark, toDark, progress);

    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, fromDark);
    bg.addColorStop(0.5, midColor);
    bg.addColorStop(1, toDark);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // ── Subtle warm base tint so it never feels cold ────────────────────────
    ctx.save();
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = "#3a1a00";
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // ── Particles ───────────────────────────────────────────────────────────
    this._drawParticles(ctx);

    // ── Travel text ─────────────────────────────────────────────────────────
    this._drawTravelText(ctx, w, h);

    // ── Tip text ────────────────────────────────────────────────────────────
    this._drawTipText(ctx, w, h);

    // ── Progress bar ────────────────────────────────────────────────────────
    this._drawProgressBar(ctx, w, h, progress);
  }

  private _drawZoneTitle(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const cfg = this.config!;
    const t = Math.min(1, this.phaseTimer / ZONE_TITLE_MS);

    // ── Background ──────────────────────────────────────────────────────────
    const deepDark = darkenColor(cfg.toZoneColor, 0.78);
    const lessDark = darkenColor(cfg.toZoneColor, 0.68);
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, deepDark);
    bg.addColorStop(1, lessDark);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Warm overlay
    ctx.save();
    ctx.globalAlpha = 0.05;
    ctx.fillStyle = "#2a1000";
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // ── Danger vignette ─────────────────────────────────────────────────────
    if (cfg.toZoneDangerLevel >= 2) {
      const dangerStrength = cfg.toZoneDangerLevel >= 3 ? 0.25 : 0.15;
      const dvig = ctx.createRadialGradient(w * 0.5, h * 0.5, h * 0.3, w * 0.5, h * 0.5, h * 0.8);
      dvig.addColorStop(0, "rgba(0,0,0,0)");
      dvig.addColorStop(1, `rgba(100,8,4,${dangerStrength})`);
      ctx.save();
      ctx.fillStyle = dvig;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // ── Remaining particles (carry over from traveling) ─────────────────────
    if (this.particles.length > 0) {
      this._drawParticles(ctx);
    }

    // ── Radial glow behind title ─────────────────────────────────────────────
    const glowAlpha = Math.min(t * 3, 1) * 0.08;
    if (glowAlpha > 0) {
      const cx = w * 0.5;
      const cy = h * 0.5;
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, h * 0.55);
      glow.addColorStop(0, hexAlpha(cfg.toZoneColor, glowAlpha));
      glow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.save();
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // ── Zone-specific vignette art ───────────────────────────────────────────
    this._drawZoneArt(ctx, w, h, cfg, t);

    // ── Text block layout ────────────────────────────────────────────────────
    // Zone name fades in over first 0.4s, settles from scale 1.1 → 1.0
    const nameT   = Math.min(1, t / 0.20);       // 0–1 over first 20% of phase
    const nameAlpha = easeOutQuad(nameT);
    const nameScale = 1.1 - easeInOutCubic(nameT) * 0.1;

    // Description fades in starting at t=0.15
    const descT   = Math.max(0, Math.min(1, (t - 0.15) / 0.15));
    const descAlpha = easeOutQuad(descT);

    // Lines & ornaments fade in alongside the name
    const lineAlpha = easeOutQuad(Math.min(1, t / 0.25)) * 0.3;

    const cx = w * 0.5;
    const cy = h * 0.5;

    // Measure zone name
    ctx.save();
    ctx.font = `bold ${Math.round(16)}px Georgia, 'Times New Roman', serif`;
    const nameMetrics = ctx.measureText(cfg.toZoneName);
    const nameW = nameMetrics.width * nameScale;
    ctx.restore();

    // Vertical layout: line gap + name + gap + desc + gap + danger
    const lineGap   = 14;
    const nameFontH = 16;
    const descFontH = 9;
    const dangerH   = cfg.toZoneDangerLevel >= 2 ? 14 : 0;
    const totalH    = lineGap + nameFontH + 10 + descFontH + (dangerH > 0 ? 10 + dangerH : 0) + lineGap;
    const blockTop  = cy - totalH * 0.5;

    let drawY = blockTop;

    // ── Decorative line above ────────────────────────────────────────────────
    drawY += lineGap * 0.5;
    this._drawDecorativeLine(ctx, cx, drawY, Math.min(w * 0.5, nameW + 60), lineAlpha);
    drawY += lineGap * 0.5;

    // ── Zone name ────────────────────────────────────────────────────────────
    drawY += nameFontH;
    if (nameAlpha > 0.005) {
      ctx.save();
      ctx.globalAlpha = nameAlpha;
      ctx.font = `bold 16px Georgia, 'Times New Roman', serif`;
      ctx.fillStyle = P.zoneName;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 3;

      // Scale settle from center
      ctx.translate(cx, drawY);
      ctx.scale(nameScale, nameScale);
      ctx.fillText(cfg.toZoneName, 0, 0);
      ctx.restore();
    }
    drawY += 10;

    // ── Zone description ─────────────────────────────────────────────────────
    if (descAlpha > 0.005 && cfg.toZoneDescription) {
      const maxDescW = w * 0.60;
      ctx.save();
      ctx.globalAlpha = descAlpha;
      ctx.font = `italic 9px Georgia, 'Times New Roman', serif`;
      ctx.fillStyle = P.zoneDesc;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.shadowColor = "rgba(0,0,0,0.4)";
      ctx.shadowBlur = 2;

      const lines = this._wrapText(ctx, cfg.toZoneDescription, maxDescW);
      for (const line of lines) {
        ctx.fillText(line, cx, drawY + descFontH);
        drawY += descFontH + 3;
      }
      ctx.restore();
    } else {
      drawY += descFontH;
    }

    // ── Danger indicator ─────────────────────────────────────────────────────
    if (cfg.toZoneDangerLevel >= 2 && descAlpha > 0.1) {
      drawY += 6;
      const warnings = "⚠".repeat(cfg.toZoneDangerLevel);
      ctx.save();
      ctx.globalAlpha = descAlpha * 0.8;
      ctx.font = `8px Arial, sans-serif`;
      ctx.fillStyle = P.dangerText;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.shadowColor = "rgba(0,0,0,0.4)";
      ctx.shadowBlur = 2;
      ctx.fillText(`Danger Level: ${warnings}`, cx, drawY + 9);
      drawY += dangerH;
      ctx.restore();
    }

    // ── Decorative line below ────────────────────────────────────────────────
    drawY += lineGap * 0.5;
    this._drawDecorativeLine(ctx, cx, drawY, Math.min(w * 0.5, nameW + 60), lineAlpha);
  }

  private _drawFadeIn(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const cfg = this.config;
    // If config is null at this point (edge case), bail gracefully
    if (!cfg) return;

    const t = Math.min(1, this.phaseTimer / FADE_IN_MS);
    const eased = easeOutQuad(t);

    // Destination zone background receding
    const deepDark = darkenColor(cfg.toZoneColor, 0.78);
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, deepDark);
    bg.addColorStop(1, darkenColor(cfg.toZoneColor, 0.68));

    ctx.save();
    ctx.globalAlpha = 1 - eased;
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // Warm black overlay that lifts away
    ctx.save();
    ctx.globalAlpha = Math.max(0, (1 - eased) * 0.85);
    ctx.fillStyle = P.blackWarm;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  // ─── Drawing Helpers ─────────────────────────────────────────────────────────

  /** Draw all living particles */
  private _drawParticles(ctx: CanvasRenderingContext2D): void {
    const now = this.totalTimer;
    for (const p of this.particles) {
      const breathing = 0.6 + 0.4 * Math.sin(now * 0.002 + p.phase);
      const alpha = p.baseAlpha * breathing * (0.4 + 0.6 * p.life / p.maxLife);

      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  /** Draw travel destination text in center */
  private _drawTravelText(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const cfg = this.config!;
    const cx = w * 0.5;
    const cy = h * 0.5;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    // "Traveling to..." label
    ctx.globalAlpha = 0.85;
    ctx.font = `italic 10px Georgia, 'Times New Roman', serif`;
    ctx.fillStyle = P.travelText;
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 4;
    ctx.fillText("Traveling to...", cx, cy - 10);

    // Zone name
    ctx.globalAlpha = 1.0;
    ctx.font = `bold 14px Georgia, 'Times New Roman', serif`;
    ctx.fillStyle = P.zoneName;
    ctx.shadowBlur = 6;
    ctx.fillText(cfg.toZoneName, cx, cy + 8);

    ctx.restore();
  }

  /** Draw the tip text area with fade transitions */
  private _drawTipText(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    if (!this.currentTip) return;

    const tipY = h - 28;
    const cx = w * 0.5;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.font = `8px Arial, Helvetica, sans-serif`;
    ctx.fillStyle = P.tipText;
    ctx.globalAlpha = this.tipAlpha * 0.9;
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur = 3;

    ctx.fillText(this.currentTip, cx, tipY);
    ctx.restore();
  }

  /** Draw the progress bar at bottom center */
  private _drawProgressBar(ctx: CanvasRenderingContext2D, w: number, h: number, progress: number): void {
    const barW  = Math.round(w * 0.50);
    const barH  = 2;
    const barX  = (w - barW) * 0.5;
    const barY  = h - 14;
    const dotR  = 2.5;
    const trackR = 1.5;

    // Track (dim background line)
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = P.progressGold;
    ctx.lineWidth = barH;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(barX, barY);
    ctx.lineTo(barX + barW, barY);
    ctx.stroke();
    ctx.restore();

    // Filled progress
    if (progress > 0) {
      const fillW = Math.max(0, barW * progress);
      ctx.save();
      const grad = ctx.createLinearGradient(barX, 0, barX + fillW, 0);
      grad.addColorStop(0, hexAlpha(P.progressGold, 0.20));
      grad.addColorStop(1, hexAlpha(P.progressGold, 0.55));
      ctx.globalAlpha = 1;
      ctx.strokeStyle = grad;
      ctx.lineWidth = barH;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(barX, barY);
      ctx.lineTo(barX + fillW, barY);
      ctx.stroke();
      ctx.restore();
    }

    // Endpoint dots
    for (const x of [barX, barX + barW]) {
      ctx.save();
      ctx.globalAlpha = 0.30;
      ctx.fillStyle = P.progressGold;
      ctx.beginPath();
      ctx.arc(x, barY, trackR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Sliding position dot
    const dotX = barX + barW * progress;
    ctx.save();
    ctx.globalAlpha = 0.90;
    ctx.fillStyle = P.progressGold;
    ctx.shadowColor = P.progressGold;
    ctx.shadowBlur = 5;
    ctx.beginPath();
    ctx.arc(dotX, barY, dotR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /**
   * Draw a thin horizontal decorative line centered at (cx, y) with a
   * small diamond ornament at its midpoint.
   */
  private _drawDecorativeLine(
    ctx: CanvasRenderingContext2D,
    cx: number,
    y: number,
    halfWidth: number,
    alpha: number
  ): void {
    if (alpha < 0.005) return;

    const left  = cx - halfWidth;
    const right = cx + halfWidth;
    const diamondSize = 3;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = P.goldLine;
    ctx.lineWidth = 1;

    // Left segment
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(cx - diamondSize * 1.5, y);
    ctx.stroke();

    // Right segment
    ctx.beginPath();
    ctx.moveTo(cx + diamondSize * 1.5, y);
    ctx.lineTo(right, y);
    ctx.stroke();

    // Diamond ornament
    ctx.fillStyle = P.goldLine;
    ctx.beginPath();
    ctx.moveTo(cx, y - diamondSize);
    ctx.lineTo(cx + diamondSize, y);
    ctx.lineTo(cx, y + diamondSize);
    ctx.lineTo(cx - diamondSize, y);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  // ─── Zone-Specific Art ───────────────────────────────────────────────────────

  /**
   * Draw a subtle procedural vignette illustration behind the zone title.
   * Each zone type gets a unique atmospheric visual — silhouettes, ambient
   * shapes, and thematic motifs that set the mood before arrival.
   */
  private _drawZoneArt(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    cfg: TransitionConfig,
    t: number,
  ): void {
    const alpha = Math.min(1, t * 2.5) * 0.15;
    if (alpha < 0.005) return;

    const cx = w * 0.5;
    const cy = h * 0.5;
    const [cr, cg, cb] = hexToRgb(cfg.toZoneColor);
    const zoneColor = `rgba(${cr},${cg},${cb},`;

    ctx.save();

    // Identify zone by name patterns for custom art
    const name = cfg.toZoneName.toLowerCase();

    if (name.includes("proving") || name.includes("training")) {
      // ── Training Grounds — crossed swords + torch flames ──────────
      ctx.globalAlpha = alpha * 1.2;
      ctx.strokeStyle = zoneColor + "0.6)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";

      // Left sword
      ctx.save();
      ctx.translate(cx - 30, cy);
      ctx.rotate(-0.4);
      ctx.beginPath();
      ctx.moveTo(0, -35); ctx.lineTo(0, 25); // blade
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-8, 18); ctx.lineTo(8, 18); // guard
      ctx.stroke();
      ctx.fillStyle = zoneColor + "0.4)";
      ctx.fillRect(-3, 18, 6, 14); // grip
      ctx.restore();

      // Right sword (mirrored)
      ctx.save();
      ctx.translate(cx + 30, cy);
      ctx.rotate(0.4);
      ctx.beginPath();
      ctx.moveTo(0, -35); ctx.lineTo(0, 25);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-8, 18); ctx.lineTo(8, 18);
      ctx.stroke();
      ctx.fillStyle = zoneColor + "0.4)";
      ctx.fillRect(-3, 18, 6, 14);
      ctx.restore();

      // Torch flames (flickering)
      for (const side of [-1, 1]) {
        const tx = cx + side * 70;
        const ty = cy + 10;
        const flicker = Math.sin(this.totalTimer * 0.006 + side * 2) * 3;
        ctx.globalAlpha = alpha * 0.8;
        // Flame body
        ctx.beginPath();
        ctx.moveTo(tx, ty - 20 + flicker);
        ctx.quadraticCurveTo(tx + 8, ty - 8, tx, ty + 5);
        ctx.quadraticCurveTo(tx - 8, ty - 8, tx, ty - 20 + flicker);
        ctx.fillStyle = `rgba(255,180,60,${alpha * 2})`;
        ctx.fill();
        // Torch post
        ctx.fillStyle = zoneColor + "0.3)";
        ctx.fillRect(tx - 2, ty + 5, 4, 20);
      }

    } else if (name.includes("harbor") || name.includes("moonlit")) {
      // ── Harbor — waves + anchor ───────────────────────────────────
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = zoneColor + "0.5)";
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 5; i++) {
        const wy = cy + 25 + i * 10;
        const waveOffset = Math.sin(this.totalTimer * 0.003 + i) * 8;
        ctx.beginPath();
        for (let x = 0; x < w; x += 20) {
          const wv = Math.sin((x + waveOffset) * 0.08) * 4;
          if (x === 0) ctx.moveTo(x, wy + wv);
          else ctx.lineTo(x, wy + wv);
        }
        ctx.stroke();
      }

    } else if (name.includes("forest") || name.includes("whisper")) {
      // ── Forest — tree silhouettes ─────────────────────────────────
      ctx.globalAlpha = alpha * 0.8;
      ctx.fillStyle = zoneColor + "0.3)";
      for (let i = 0; i < 7; i++) {
        const treeX = w * 0.1 + i * (w * 0.12);
        const treeH = 30 + (i % 3) * 15;
        const treeY = h * 0.7;
        // Triangle tree
        ctx.beginPath();
        ctx.moveTo(treeX, treeY - treeH);
        ctx.lineTo(treeX + treeH * 0.35, treeY);
        ctx.lineTo(treeX - treeH * 0.35, treeY);
        ctx.closePath();
        ctx.fill();
        // Trunk
        ctx.fillRect(treeX - 2, treeY, 4, 10);
      }

    } else if (name.includes("peak") || name.includes("ashen")) {
      // ── Mountain — peak silhouettes ───────────────────────────────
      ctx.globalAlpha = alpha;
      ctx.fillStyle = zoneColor + "0.25)";
      const peaks = [
        { x: cx - 60, h: 55 }, { x: cx - 20, h: 70 },
        { x: cx + 25, h: 50 }, { x: cx + 60, h: 40 },
      ];
      for (const pk of peaks) {
        const base = h * 0.7;
        ctx.beginPath();
        ctx.moveTo(pk.x - 35, base);
        ctx.lineTo(pk.x, base - pk.h);
        ctx.lineTo(pk.x + 35, base);
        ctx.closePath();
        ctx.fill();
      }

    } else if (name.includes("crystal") || name.includes("root")) {
      // ── Underground — crystal formations ──────────────────────────
      ctx.globalAlpha = alpha * 0.9;
      for (let i = 0; i < 8; i++) {
        const crX = cx + (i - 4) * 25;
        const crH = 15 + (i % 3) * 12;
        const crY = cy + 30;
        ctx.fillStyle = zoneColor + `${0.2 + (i % 3) * 0.1})`;
        ctx.beginPath();
        ctx.moveTo(crX, crY);
        ctx.lineTo(crX + 4, crY - crH);
        ctx.lineTo(crX + 8, crY);
        ctx.closePath();
        ctx.fill();
      }

    } else {
      // ── Default — subtle radial motif ─────────────────────────────
      ctx.globalAlpha = alpha * 0.5;
      ctx.strokeStyle = zoneColor + "0.3)";
      ctx.lineWidth = 0.5;
      for (let ring = 0; ring < 3; ring++) {
        ctx.beginPath();
        ctx.arc(cx, cy, 30 + ring * 20, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  // ─── Particle System ─────────────────────────────────────────────────────────

  /** Seed the initial particle population when traveling begins */
  private _seedParticles(): void {
    const w = this._width  || 480;
    const h = this._height || 270;
    const cfg = this.config!;
    const count = 30 + Math.floor(Math.random() * 11); // 30–40

    this.particles = [];
    for (let i = 0; i < count; i++) {
      // Distribute across screen, not just left edge, for an immediate field
      this.particles.push(spawnParticle(w, h, cfg.toZoneColor, false));
    }
  }

  /** Update all particles — move, age, recycle */
  private _updateParticles(dt: number): void {
    const w = this._width  || 480;
    const h = this._height || 270;
    const cfg = this.config!;
    const target = 30 + Math.floor(Math.random() * 3); // maintain ~30–32

    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.life -= dt;

      const escaped = p.x - p.size > w || p.y + p.size < 0 || p.y - p.size > h;
      const dead    = p.life <= 0;

      if (escaped || dead) {
        respawnAtLeftEdge(p, w, h, cfg.toZoneColor);
      }
    }

    // Top up if below target
    while (this.particles.length < target) {
      this.particles.push(spawnParticle(w, h, cfg.toZoneColor, true));
    }
  }

  // ─── Tip Cycling ─────────────────────────────────────────────────────────────

  private _updateTip(dt: number): void {
    const FADE_MS = 400; // ms for each fade half

    if (this.tipFading) {
      // Fading out
      this.tipAlpha = Math.max(0, this.tipAlpha - dt / FADE_MS);
      if (this.tipAlpha <= 0) {
        // Swap to next tip and fade back in
        this.currentTip = this.nextTip;
        this.tipFading  = false;
        this.tipAlpha   = 0;
        this.tipTimer   = 0;
      }
    } else {
      // Fading in (if alpha is below 1)
      if (this.tipAlpha < 1) {
        this.tipAlpha = Math.min(1, this.tipAlpha + dt / FADE_MS);
      }

      // Check if it's time to cycle
      this.tipTimer += dt;
      if (this.tipTimer >= TIP_CYCLE_MS) {
        this.nextTip   = this._pickTip();
        this.tipFading = true;
      }
    }
  }

  /** Pick a random tip that is different from the current one */
  private _pickTip(): string {
    const pool = TRAVEL_TIPS.filter(t => t !== this.currentTip);
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // ─── Text Utilities ───────────────────────────────────────────────────────────

  /**
   * Word-wrap `text` to fit within `maxWidth` pixels using the current ctx font.
   * Returns an array of line strings.
   */
  private _wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";

    for (const word of words) {
      const test = current ? current + " " + word : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }
}
